export enum CardType {
  VERVE = 'VERVE',
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Detects card type based on PAN (Primary Account Number)
 * Supports Nigerian card types: Verve, Visa, Mastercard
 *
 * @param pan - The full card number
 * @returns CardType enum value
 *
 * Reference:
 * - Verve: IIN 506 & 507, Length 19
 * - Visa: First digit 4, Length 13 or 16 or 19
 * - Mastercard: First 2 digits 51-55 or 2221-2720, Length 16
 */
export const getCardType = (pan: string): CardType => {
  if (!pan) return CardType.UNKNOWN;

  // Remove spaces and hyphens
  const cleanPan = pan.replace(/[\s-]/g, '');

  const firstDigit = cleanPan.charAt(0);
  const first2Digits = cleanPan.substring(0, 2);
  const first4Digits = cleanPan.substring(0, 4);
  const first6Digits = cleanPan.substring(0, 6);
  const length = cleanPan.length;

  // Verve: IIN 506 or 507, Length typically 19
  if ((first6Digits === '506397' || first6Digits === '507856' ||
    first4Digits === '5060' || first4Digits === '5061') && length === 19) {
    return CardType.VERVE;
  }

  // Verve alternative: First 4 digits starting with 506 or 507
  if ((first4Digits.startsWith('506') || first4Digits.startsWith('507')) &&
    (length === 16 || length === 19)) {
    return CardType.VERVE;
  }

  // Visa: First digit 4, Length 13, 16, or 19
  if (firstDigit === '4' || (length === 13 || length === 16 || length === 19)) {
    return CardType.VISA;
  }

  // Mastercard: First 2 digits 51-55 or 2221-2720, Length 16
  const first2DigitsNum = parseInt(first2Digits, 10);
  if ((first2DigitsNum >= 51 && first2DigitsNum <= 55) && length === 16) {
    return CardType.MASTERCARD;
  }

  // Mastercard new range: 2221-2720
  const first4DigitsNum = parseInt(first4Digits, 10);
  if (first4DigitsNum >= 2221 && first4DigitsNum <= 2720 && length === 16) {
    return CardType.MASTERCARD;
  }

  return CardType.UNKNOWN;
};

/**
 * Whether the BIN lookup brand is AfriGo. Its casing varies, e.g. "AfriGo".
 */
export const isAfrigoCard = (brand?: string | null): boolean =>
  !!brand && brand.replace(/[^a-z]/gi, '').toUpperCase() === 'AFRIGO';

/**
 * Validates card PAN using Luhn algorithm
 *
 * @param pan - The card number to validate
 * @returns true if valid, false otherwise
 */
export const validatePAN = (pan: string): boolean => {
  const cleanPan = pan.replace(/[\s-]/g, '');

  if (!/^\d+$/.test(cleanPan)) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  for (let i = cleanPan.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanPan.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Gets card display name based on type
 *
 * @param cardType - The card type
 * @returns Formatted card type name
 */
export const getCardDisplayName = (cardType: CardType): string => {
  switch (cardType) {
    case CardType.VERVE:
      return 'Verve';
    case CardType.VISA:
      return 'Visa';
    case CardType.MASTERCARD:
      return 'Mastercard';
    default:
      return 'Unknown Card';
  }
};

export const getDeviceInformation = () => {
  return {
    httpBrowserLanguage: navigator.language || navigator.language,
    httpBrowserJavaEnabled: navigator.javaEnabled
      ? navigator.javaEnabled()
      : false,
    httpBrowserJavaScriptEnabled: true,
    httpBrowserColorDepth:
      typeof window !== 'undefined' && window.screen
        ? window.screen.colorDepth
        : 24,
    httpBrowserScreenHeight:
      typeof window !== 'undefined' && window.screen ? window.screen.height : 0,
    httpBrowserScreenWidth:
      typeof window !== 'undefined' && window.screen ? window.screen.width : 0,
    httpBrowserTimeDifference: new Date().getTimezoneOffset(),
    userAgentBrowserValue: navigator.userAgent,
    deviceChannel: 'browser',
  }
};
